import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { ToastContainer } from "react-toastify";
import { Button, Plus, Select, TimeField, Trash01, XClose } from "@opendate-app/setlist";
import client from "../../shared/api/client";
import { toastError } from "../../shared/toastHelper";
import StaffingSettingsLayout from "./components/StaffingSettingsLayout";
import HeadcountStepper from "./components/HeadcountStepper";
import { errorMessages } from "./components/staffingFormatters";
import {
  ANCHOR_OPTIONS,
  clockFromMinutes,
  lineTimes,
  offsetOptionsFor,
  perShowCost,
  roundedHours,
  wholeDollars,
} from "./components/templateFormatters";

const SAVE_DELAY_MS = 500;
const EVENT_COLUMNS = "minmax(170px,1.4fr) 120px minmax(200px,1.6fr) minmax(200px,1.6fr) minmax(110px,1fr) 48px";
const WEEK_COLUMNS = "minmax(170px,1.3fr) 120px 260px 150px 150px minmax(110px,1fr) 48px";
const WEEK_DAYS = [
  { day: 1, short: "M", name: "Monday" },
  { day: 2, short: "T", name: "Tuesday" },
  { day: 3, short: "W", name: "Wednesday" },
  { day: 4, short: "T", name: "Thursday" },
  { day: 5, short: "F", name: "Friday" },
  { day: 6, short: "S", name: "Saturday" },
  { day: 0, short: "S", name: "Sunday" },
];
const NEW_WEEK_LINE = { headcount: 1, day_of_week: 1, start_minute_of_day: 17 * 60, end_minute_of_day: 23 * 60 };
const NEW_LINE = { headcount: 1, start_anchor: "doors", start_offset_minutes: -30, end_anchor: "show_end", end_offset_minutes: 30 };

const card = {
  background: "var(--sl-bg-primary)",
  border: "1px solid var(--sl-border-secondary)",
  borderRadius: "var(--sl-radius-xl)",
  boxShadow: "var(--sl-shadow-xs)",
};
const sectionLabel = { padding: "12px 16px 4px", fontSize: 12, lineHeight: "18px", color: "var(--sl-text-quaternary)" };
const headerCell = { padding: "12px 8px", fontSize: 12, lineHeight: "18px", color: "var(--sl-text-quaternary)" };
const dot = (color) => ({ width: 8, height: 8, borderRadius: "50%", background: color || "var(--sl-fg-quaternary)", flexShrink: 0 });

const TemplateListItem = ({ template, selected, onSelect }) => (
  <button
    type="button"
    onClick={onSelect}
    style={{
      width: "100%",
      textAlign: "left",
      border: "none",
      background: selected ? "var(--sl-bg-brand-primary)" : "transparent",
      padding: "10px 16px",
      cursor: "pointer",
      display: "flex",
      flexDirection: "column",
    }}
  >
    <span style={{ fontSize: 14, lineHeight: "20px", fontWeight: 600, color: selected ? "var(--sl-text-brand)" : "var(--sl-text-primary)" }}>{template.name}</span>
    <span style={{ fontSize: 12, lineHeight: "18px", color: "var(--sl-text-quaternary)" }}>
      {template.kind === "event" ? `${template.location_label} · ${template.band}` : `${template.location_label} · ${template.rows.length} ${template.rows.length === 1 ? "row" : "rows"}`}
    </span>
  </button>
);

TemplateListItem.propTypes = {
  template: PropTypes.object.isRequired,
  selected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
};

const EventLineRow = ({ line, sample, positionOptions, positionsById, onChange, onRemove }) => {
  const times = lineTimes(line, sample);
  const position = positionsById[line.staff_position_id];
  const options = positionOptions.filter((option) => !option.archived || option.value === line.staff_position_id);
  return (
    <div style={{ display: "grid", gridTemplateColumns: EVENT_COLUMNS, alignItems: "center", borderBottom: "1px solid var(--sl-border-secondary)", fontSize: 14, lineHeight: "20px", color: "var(--sl-text-tertiary)" }}>
      <div style={{ padding: "8px 8px 8px 24px", display: "flex", alignItems: "center", gap: 8 }}>
        <span style={dot(position?.color_hex)} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Select id={`line-${line.id}-position`} size="sm" options={options} value={line.staff_position_id} onChange={(value) => value && onChange({ staff_position_id: value })} />
        </div>
      </div>
      <div style={{ padding: 8 }}>
        <HeadcountStepper label={position ? position.label : "people"} value={line.headcount} onChange={(headcount) => onChange({ headcount })} />
      </div>
      {["start", "end"].map((edge) => (
        <div key={edge} style={{ padding: 8, display: "flex", gap: 6 }}>
          <Select id={`line-${line.id}-${edge}-anchor`} size="sm" width={124} options={ANCHOR_OPTIONS} value={line[`${edge}_anchor`]} onChange={(value) => value && onChange({ [`${edge}_anchor`]: value })} />
          <Select
            id={`line-${line.id}-${edge}-offset`}
            size="sm"
            width={116}
            options={offsetOptionsFor(line[`${edge}_offset_minutes`])}
            value={String(line[`${edge}_offset_minutes`])}
            onChange={(value) => value !== null && value !== undefined && value !== "" && onChange({ [`${edge}_offset_minutes`]: Number(value) })}
          />
        </div>
      ))}
      <div style={{ padding: 8, display: "flex", flexDirection: "column" }}>
        <span style={{ color: "var(--sl-text-primary)", fontWeight: 600, whiteSpace: "nowrap" }}>{times ? `${clockFromMinutes(times.start)}–${clockFromMinutes(times.end)}` : "—"}</span>
        <span style={{ fontSize: 12, lineHeight: "18px", color: "var(--sl-text-quaternary)" }}>{times ? `${roundedHours((times.end - times.start) / 60)} h each` : ""}</span>
      </div>
      <div style={{ padding: "8px 16px 8px 0" }}>
        <Button variant="tertiary" size="sm" icon={XClose} aria-label="Remove line" title="Remove line" onClick={onRemove} />
      </div>
    </div>
  );
};

EventLineRow.propTypes = {
  line: PropTypes.object.isRequired,
  sample: PropTypes.object.isRequired,
  positionOptions: PropTypes.array.isRequired,
  positionsById: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

// A row is one line per day sharing a position, hours and headcount; changing it changes every one of those lines.
const WeekRow = ({ row, positionOptions, onChange, onToggleDay, onRemove, disabled }) => {
  const days = [...new Set(row.lines.map((line) => line.day_of_week))];
  const options = positionOptions.filter((option) => !option.archived || option.value === row.staff_position_id);
  const hours = (((row.end_minute_of_day - row.start_minute_of_day + 1440) % 1440) || 1440) / 60;
  const key = row.line_ids.join("-");
  return (
    <div className="week-row" style={{ display: "grid", gridTemplateColumns: WEEK_COLUMNS, alignItems: "center", borderBottom: "1px solid var(--sl-border-secondary)", fontSize: 14, lineHeight: "20px", color: "var(--sl-text-tertiary)" }}>
      <div style={{ padding: "8px 8px 8px 24px", display: "flex", alignItems: "center", gap: 8 }}>
        <span style={dot(row.color_hex)} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Select id={`week-row-${key}-position`} size="sm" disabled={disabled} options={options} value={row.staff_position_id} onChange={(value) => value && onChange({ staff_position_id: value })} />
        </div>
      </div>
      <div style={{ padding: 8 }}>
        <HeadcountStepper compact disabled={disabled} label={row.name} value={row.headcount} onChange={(headcount) => onChange({ headcount })} />
      </div>
      <div style={{ padding: 8, display: "flex", gap: 4 }}>
        {WEEK_DAYS.map(({ day, short, name }) => {
          const on = days.includes(day);
          return (
            <Button
              key={day}
              variant={on ? "secondary-color" : "secondary"}
              size="sm"
              aria-label={name}
              aria-pressed={on}
              disabled={disabled}
              onClick={() => onToggleDay(day, on)}
            >
              {short}
            </Button>
          );
        })}
      </div>
      {["start", "end"].map((edge) => (
        <div key={edge} style={{ padding: 8 }}>
          <TimeField
            id={`week-row-${key}-${edge}`}
            size="sm"
            minuteStep={15}
            disabled={disabled}
            value={row[`${edge}_minute_of_day`]}
            onChange={(value) => {
              const minutes = typeof value === "string" ? Number(value.slice(0, 2)) * 60 + Number(value.slice(3, 5)) : value;
              if (Number.isFinite(minutes)) onChange({ [`${edge}_minute_of_day`]: minutes });
            }}
          />
        </div>
      ))}
      <div style={{ padding: 8, display: "flex", flexDirection: "column" }}>
        <span style={{ color: "var(--sl-text-primary)", fontWeight: 600, whiteSpace: "nowrap" }}>{row.hours}</span>
        <span style={{ fontSize: 12, lineHeight: "18px", color: "var(--sl-text-quaternary)" }}>{`${roundedHours(hours)} h · ${row.headcount * row.lines.length} ${row.headcount * row.lines.length === 1 ? "shift" : "shifts"}`}</span>
      </div>
      <div style={{ padding: "8px 16px 8px 0" }}>
        <Button variant="tertiary" size="sm" icon={XClose} aria-label="Remove row" title="Remove row" disabled={disabled} onClick={onRemove} />
      </div>
    </div>
  );
};

WeekRow.propTypes = {
  row: PropTypes.object.isRequired,
  positionOptions: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  onToggleDay: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

const StaffingTemplatesApp = (props) => {
  const { tabPaths, paths, permissions, locationOptions, venueOptions, bandOptions, positionOptions, rates } = props;
  const [templates, setTemplates] = useState(props.templates);
  const [selectedId, setSelectedId] = useState(props.templates[0]?.id ?? null);
  const [nameHover, setNameHover] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(null);
  const timers = useRef({});
  const queued = useRef({});
  const inFlight = useRef(0);
  const latestRequest = useRef(0);
  const requests = useRef(new Set());
  const creating = useRef(false);

  const positionsById = Object.fromEntries(positionOptions.map((option) => [option.value, option]));
  const selected = templates.find((template) => template.id === selectedId) || templates[0];
  const eventTemplates = templates.filter((template) => template.kind === "event");
  const weekTemplates = templates.filter((template) => template.kind === "week");

  const busy = () => inFlight.current > 0 || Object.keys(timers.current).length > 0;

  const refresh = useCallback(async () => {
    const requestId = ++latestRequest.current;
    try {
      const { data } = await client.get(paths.index);
      if (!busy() && requestId === latestRequest.current) setTemplates(data.templates);
    } catch (error) {
      // Keep what's on screen; the next save will try again.
    }
  }, [paths.index]);

  // Takes the server's copy (match counts, labels) only once nothing typed since is still waiting to save,
  // and only the newest reply; an older reply arriving last means fetching the current copy.
  const send = useCallback((method, url, body) => {
    const requestId = ++latestRequest.current;
    inFlight.current += 1;
    const request = (async () => {
      try {
        const { data } = await client[method](url, body);
        inFlight.current -= 1;
        if (!busy()) {
          if (requestId === latestRequest.current) setTemplates(data.templates);
          else refresh();
        }
        return data;
      } catch (error) {
        inFlight.current -= 1;
        const messages = errorMessages(error);
        toastError(messages.length ? messages.join(" ") : "Could not save the template.");
        if (!busy()) refresh();
        return null;
      }
    })();
    requests.current.add(request);
    request.finally(() => requests.current.delete(request));
    return request;
  }, [refresh]);

  const flush = useCallback((key) => {
    const pending = queued.current[key];
    delete queued.current[key];
    clearTimeout(timers.current[key]);
    delete timers.current[key];
    if (pending) send("patch", pending.url, pending.body);
  }, [send]);

  const flushAll = useCallback(() => {
    Object.keys(timers.current).forEach(flush);
    return Promise.all(Array.from(requests.current));
  }, [flush]);

  useEffect(() => () => {
    flushAll();
  }, [flushAll]);

  useEffect(() => {
    const warn = (event) => {
      if (!busy()) return;
      flushAll();
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [flushAll]);

  const queueSave = (key, url, field, attributes) => {
    const previous = queued.current[key];
    queued.current[key] = { url, body: { [field]: { ...(previous?.body[field] || {}), ...attributes } } };
    clearTimeout(timers.current[key]);
    timers.current[key] = setTimeout(() => flush(key), SAVE_DELAY_MS);
  };

  const templateUrl = (template) => `${paths.templates}/${template.id}`;
  const linesUrl = (template) => `${templateUrl(template)}/lines`;

  const updateTemplate = (template, changes, attributes = changes) => {
    setTemplates((current) => current.map((item) => (item.id === template.id ? { ...item, ...changes } : item)));
    queueSave(`template:${template.id}`, templateUrl(template), "shift_template", attributes);
  };

  // A blank name can't be saved, so it stays on screen until there's something to save.
  const renameTemplate = (template, name) => {
    if (name.trim() === "") {
      const key = `template:${template.id}`;
      const pending = queued.current[key];
      if (pending) {
        delete pending.body.shift_template.name;
        if (Object.keys(pending.body.shift_template).length === 0) {
          clearTimeout(timers.current[key]);
          delete timers.current[key];
          delete queued.current[key];
        }
      }
      setTemplates((current) => current.map((item) => (item.id === template.id ? { ...item, name } : item)));
      return;
    }
    updateTemplate(template, { name });
  };

  const updateLine = (template, line, changes) => {
    if (Object.entries(changes).every(([key, value]) => line[key] === value)) return;
    setTemplates((current) =>
      current.map((item) =>
        item.id === template.id ? { ...item, lines: item.lines.map((other) => (other.id === line.id ? { ...other, ...changes } : other)) } : item,
      ),
    );
    queueSave(`line:${line.id}`, `${linesUrl(template)}/${line.id}`, "shift_template_line", changes);
  };

  const updateWeekRow = (template, row, changes) => {
    if (Object.entries(changes).every(([key, value]) => row[key] === value)) return;
    const position = changes.staff_position_id && positionsById[changes.staff_position_id];
    const shown = position ? { ...changes, name: position.label, color_hex: position.color_hex } : changes;
    setTemplates((current) =>
      current.map((item) =>
        item.id === template.id ? { ...item, rows: item.rows.map((other) => (other.line_ids === row.line_ids ? { ...other, ...shown } : other)) } : item,
      ),
    );
    row.line_ids.forEach((lineId) => queueSave(`line:${lineId}`, `${linesUrl(template)}/${lineId}`, "shift_template_line", changes));
  };

  const dropQueued = (lineId) => {
    delete queued.current[`line:${lineId}`];
    clearTimeout(timers.current[`line:${lineId}`]);
    delete timers.current[`line:${lineId}`];
  };

  const removeWeekRow = (template, row) => {
    setTemplates((current) => current.map((item) => (item.id === template.id ? { ...item, rows: item.rows.filter((other) => other.line_ids !== row.line_ids) } : item)));
    row.line_ids.forEach((lineId) => {
      dropQueued(lineId);
      send("delete", `${linesUrl(template)}/${lineId}`);
    });
  };

  // Saves the row's other edits first, so the new day copies the hours on screen.
  const toggleWeekDay = async (template, row, day, on) => {
    if (on) {
      const lines = row.lines.filter((line) => line.day_of_week === day);
      if (lines.length === row.lines.length) return removeWeekRow(template, row);
      setTemplates((current) =>
        current.map((item) =>
          item.id === template.id
            ? {
                ...item,
                rows: item.rows.map((other) =>
                  other.line_ids === row.line_ids
                    ? { ...other, lines: other.lines.filter((line) => line.day_of_week !== day), line_ids: other.line_ids.filter((id) => !lines.some((line) => line.id === id)) }
                    : other,
                ),
              }
            : item,
        ),
      );
      return lines.forEach((line) => {
        dropQueued(line.id);
        send("delete", `${linesUrl(template)}/${line.id}`);
      });
    }
    const results = await flushAll();
    if (results.includes(null)) return null;
    const { staff_position_id, headcount, start_minute_of_day, end_minute_of_day } = row;
    return once(() => send("post", linesUrl(template), { shift_template_line: { staff_position_id, headcount, start_minute_of_day, end_minute_of_day, day_of_week: day } }));
  };

  // Starts with a position the template doesn't use yet, so the new row doesn't fold into an existing one.
  const addWeekRow = (template) => {
    const active = positionOptions.filter((option) => !option.archived);
    const first = active.find((option) => !template.rows.some((row) => row.staff_position_id === option.value)) || active[0];
    if (!first) return toastError("Add a position first, then give it a row.");
    return once(() => send("post", linesUrl(template), { shift_template_line: { ...NEW_WEEK_LINE, staff_position_id: first.value } }));
  };

  const newWeekTemplate = async () => {
    const data = await once(() => send("post", paths.templates, { shift_template: { kind: "week", name: "New week template", location: venueOptions[0]?.value } }));
    if (!data) return;
    const created = data.templates.find((template) => template.id === data.id);
    setTemplates((current) => (current.some((template) => template.id === data.id) ? current : [...current, created]));
    setSelectedId(data.id);
  };

  const deleteTemplate = async (template) => {
    setConfirmingDelete(null);
    const lineIds = template.rows.flatMap((row) => row.line_ids);
    [`template:${template.id}`, ...lineIds.map((id) => `line:${id}`)].forEach((key) => {
      clearTimeout(timers.current[key]);
      delete timers.current[key];
      delete queued.current[key];
    });
    const data = await send("delete", templateUrl(template));
    if (data) setSelectedId(data.templates[0]?.id ?? null);
  };

  // One create at a time, so a double click can't add two lines or two templates.
  const once = async (create) => {
    if (creating.current) return null;
    creating.current = true;
    try {
      return await create();
    } finally {
      creating.current = false;
    }
  };

  const addLine = (template) => {
    const first = positionOptions.find((option) => !option.archived);
    if (!first) return toastError("Add a position first, then give it a line.");
    return once(() => send("post", linesUrl(template), { shift_template_line: { ...NEW_LINE, staff_position_id: first.value } }));
  };

  const removeLine = (template, line) => {
    delete queued.current[`line:${line.id}`];
    clearTimeout(timers.current[`line:${line.id}`]);
    delete timers.current[`line:${line.id}`];
    setTemplates((current) => current.map((item) => (item.id === template.id ? { ...item, lines: item.lines.filter((other) => other.id !== line.id) } : item)));
    send("delete", `${linesUrl(template)}/${line.id}`);
  };

  const newTemplate = async () => {
    const data = await once(() => send("post", paths.templates, { shift_template: { name: "New event template", location: locationOptions[0]?.value, band: "Any capacity" } }));
    if (!data) return;
    const created = data.templates.find((template) => template.id === data.id);
    setTemplates((current) => (current.some((template) => template.id === data.id) ? current : [...current, created]));
    setSelectedId(data.id);
  };

  // Saves anything still waiting first, so Staff this week sees the template as it is on screen.
  const runStaffWeek = async (template) => {
    const results = await flushAll();
    if (results.includes(null)) return;
    const location = locationOptions.find((option) => option.value === template.location);
    const params = new URLSearchParams({ staff_week: "1" });
    if (location) params.set("venue_ownership_id", location.venue_ownership_id);
    window.location.href = `${paths.schedule}?${params}`;
  };

  // Opens the schedule at this template's venue; Apply there picks the week.
  const applyWeekTemplate = async (template) => {
    const results = await flushAll();
    if (results.includes(null)) return;
    const venue = venueOptions.find((option) => option.value === template.location);
    window.location.href = venue ? `${paths.schedule}?${new URLSearchParams({ venue_ownership_id: venue.venue_ownership_id })}` : paths.schedule;
  };

  const renderEventEditor = (template) => {
    const headcount = template.lines.reduce((sum, line) => sum + line.headcount, 0);
    const cost = permissions.readWages ? perShowCost(template.lines, template.sample, rates) : 0;
    const matches = template.match_count;
    const bands = bandOptions.some((option) => option.value === template.band) ? bandOptions : [...bandOptions, { value: template.band, label: template.band }];
    return (
      <>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--sl-border-secondary)", display: "flex", flexDirection: "column", gap: 12 }}>
          <input
            aria-label="Template name"
            disabled={template.can_manage === false}
            value={template.name}
            onChange={(event) => renameTemplate(template, event.target.value)}
            onMouseEnter={() => setNameHover(true)}
            onMouseLeave={() => setNameHover(false)}
            style={{ border: `1px solid ${nameHover ? "var(--sl-border-primary)" : "transparent"}`, borderRadius: "var(--sl-radius-md)", padding: "2px 6px", marginLeft: -7, fontFamily: "inherit", fontWeight: 600, fontSize: 18, lineHeight: "28px", color: "var(--sl-text-primary)", background: "none", width: "100%" }}
          />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
            <Select
              id={`template-${template.id}-room`}
              size="sm"
              width={150}
              options={locationOptions}
              disabled={template.can_manage === false}
              value={template.location}
              onChange={(value) => value && updateTemplate(template, { location: value, location_label: locationOptions.find((option) => option.value === value)?.label }, { location: value })}
            />
            <Select id={`template-${template.id}-band`} size="sm" width={170} disabled={template.can_manage === false} options={bands} value={template.band} onChange={(value) => value && updateTemplate(template, { band: value })} />
            <span style={{ fontSize: 13, lineHeight: "18px", color: "var(--sl-text-tertiary)" }}>
              {`Matches ${matches} confirmed show${matches === 1 ? "" : "s"} this week · ${headcount} people per show${cost ? ` · about ${wholeDollars(cost)} per show` : ""}`}
            </span>
          </div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <div style={{ minWidth: 1040 }}>
            <div style={{ display: "grid", gridTemplateColumns: EVENT_COLUMNS, background: "var(--sl-bg-secondary)", borderBottom: "1px solid var(--sl-border-secondary)" }}>
              <div style={{ ...headerCell, paddingLeft: 24 }}>Position</div>
              <div style={headerCell}>Headcount</div>
              <div style={headerCell}>Starts</div>
              <div style={headerCell}>Ends</div>
              <div style={headerCell}>{`Times for ${template.sample.title}`}</div>
              <div />
            </div>
            {template.lines.map((line) => (
              <EventLineRow
                key={line.id}
                line={line}
                sample={template.sample}
                positionOptions={positionOptions}
                positionsById={positionsById}
                onChange={(changes) => updateLine(template, line, changes)}
                onRemove={() => removeLine(template, line)}
              />
            ))}
            {template.lines.length === 0 && (
              <div style={{ padding: "32px 24px", textAlign: "center", fontSize: 13, lineHeight: "18px", color: "var(--sl-text-tertiary)" }}>
                No lines yet. Add a position, how many people, and when they start and end relative to the show.
              </div>
            )}
          </div>
        </div>
        <div style={{ padding: "16px 24px", display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "space-between", alignItems: "center" }}>
          <Button variant="link-color" icon={Plus} onClick={() => addLine(template)}>
            Add line
          </Button>
          <Button variant="secondary" onClick={() => runStaffWeek(template)}>
            Run Staff this week
          </Button>
        </div>
      </>
    );
  };

  // Rows hold only positions this member schedules; renaming, moving or deleting needs every line's position.
  const renderWeekEditor = (template) => {
    const disabled = template.can_manage === false;
    return (
      <>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--sl-border-secondary)", display: "flex", flexDirection: "column", gap: 12 }}>
          <input
            aria-label="Template name"
            disabled={disabled}
            value={template.name}
            onChange={(event) => renameTemplate(template, event.target.value)}
            onMouseEnter={() => setNameHover(true)}
            onMouseLeave={() => setNameHover(false)}
            style={{ border: `1px solid ${nameHover ? "var(--sl-border-primary)" : "transparent"}`, borderRadius: "var(--sl-radius-md)", padding: "2px 6px", marginLeft: -7, fontFamily: "inherit", fontWeight: 600, fontSize: 18, lineHeight: "28px", color: "var(--sl-text-primary)", background: "none", width: "100%" }}
          />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
            {venueOptions.length > 1 && (
              <Select
                id={`template-${template.id}-venue`}
                size="sm"
                width={220}
                options={venueOptions}
                disabled={disabled}
                value={template.location}
                onChange={(value) => value && updateTemplate(template, { location: value, location_label: venueOptions.find((option) => option.value === value)?.label }, { location: value })}
              />
            )}
            <span style={{ fontSize: 13, lineHeight: "18px", color: "var(--sl-text-tertiary)" }}>
              Week template · creates these shifts every week you apply it from the schedule. Not tied to shows.
            </span>
          </div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <div style={{ minWidth: 1000 }}>
            <div style={{ display: "grid", gridTemplateColumns: WEEK_COLUMNS, background: "var(--sl-bg-secondary)", borderBottom: "1px solid var(--sl-border-secondary)" }}>
              <div style={{ ...headerCell, paddingLeft: 24 }}>Position</div>
              <div style={headerCell}>Headcount</div>
              <div style={headerCell}>Days</div>
              <div style={headerCell}>Starts</div>
              <div style={headerCell}>Ends</div>
              <div style={headerCell}>Hours</div>
              <div />
            </div>
            {template.rows.map((row) => (
              <WeekRow
                key={row.line_ids.join("-")}
                row={row}
                positionOptions={positionOptions}
                onChange={(changes) => updateWeekRow(template, row, changes)}
                onToggleDay={(day, on) => toggleWeekDay(template, row, day, on)}
                onRemove={() => removeWeekRow(template, row)}
              />
            ))}
            {template.rows.length === 0 && (
              <div style={{ padding: "32px 24px", textAlign: "center", fontSize: 13, lineHeight: "18px", color: "var(--sl-text-tertiary)" }}>
                No rows yet. Add a position, how many people, which days and their hours.
              </div>
            )}
          </div>
        </div>
        <div style={{ padding: "16px 24px", display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "space-between", alignItems: "center" }}>
          <Button variant="link-color" icon={Plus} onClick={() => addWeekRow(template)}>
            Add row
          </Button>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {!disabled && (
              <Button variant="secondary" icon={Trash01} onClick={() => (confirmingDelete === template.id ? deleteTemplate(template) : setConfirmingDelete(template.id))}>
                {confirmingDelete === template.id ? "Click again to delete" : "Delete template"}
              </Button>
            )}
            <Button variant="secondary" onClick={() => applyWeekTemplate(template)}>
              Apply to a week
            </Button>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <StaffingSettingsLayout current="templates" title="Templates" tabPaths={tabPaths}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start" }}>
          <div style={{ ...card, flex: "1 1 260px", maxWidth: 320, overflow: "hidden" }}>
            <div style={sectionLabel}>Event templates</div>
            {eventTemplates.map((template) => (
              <TemplateListItem key={template.id} template={template} selected={template.id === selected?.id} onSelect={() => setSelectedId(template.id)} />
            ))}
            {weekTemplates.length > 0 && (
              <>
                <div style={{ ...sectionLabel, borderTop: "1px solid var(--sl-border-secondary)", marginTop: 8 }}>Week templates</div>
                {weekTemplates.map((template) => (
                  <TemplateListItem key={template.id} template={template} selected={template.id === selected?.id} onSelect={() => setSelectedId(template.id)} />
                ))}
              </>
            )}
            <div style={{ padding: "12px 16px", borderTop: "1px solid var(--sl-border-secondary)", marginTop: 8 }}>
              <div style={{ display: "grid", gap: 8 }}>
                <Button variant="secondary" icon={Plus} disabled={locationOptions.length === 0} onClick={newTemplate}>
                  New event template
                </Button>
                <Button variant="secondary" icon={Plus} disabled={venueOptions.length === 0} onClick={newWeekTemplate}>
                  New week template
                </Button>
              </div>
            </div>
          </div>
          <div style={{ ...card, flex: "3 1 1040px", minWidth: 0 }}>
            {selected?.kind === "event" && renderEventEditor(selected)}
            {selected?.kind === "week" && renderWeekEditor(selected)}
            {!selected && (
              <div style={{ padding: "32px 24px", textAlign: "center", fontSize: 13, lineHeight: "18px", color: "var(--sl-text-tertiary)" }}>
                No templates yet. Make an event template to say who works each show, then run Staff this week from the schedule.
              </div>
            )}
          </div>
        </div>
      </StaffingSettingsLayout>
      <ToastContainer icon={false} />
    </>
  );
};

StaffingTemplatesApp.propTypes = {
  templates: PropTypes.arrayOf(PropTypes.object).isRequired,
  locationOptions: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string.isRequired, label: PropTypes.string.isRequired })).isRequired,
  bandOptions: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string.isRequired, label: PropTypes.string.isRequired })).isRequired,
  venueOptions: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string.isRequired, label: PropTypes.string.isRequired })).isRequired,
  positionOptions: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.number.isRequired, label: PropTypes.string.isRequired })).isRequired,
  rates: PropTypes.object.isRequired,
  tabPaths: PropTypes.object.isRequired,
  permissions: PropTypes.shape({ readWages: PropTypes.bool.isRequired }).isRequired,
  paths: PropTypes.shape({
    index: PropTypes.string.isRequired,
    templates: PropTypes.string.isRequired,
    schedule: PropTypes.string.isRequired,
  }).isRequired,
};

export default StaffingTemplatesApp;
